import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useStaffWorkspace, type StaffWorkspace } from "../app/session";
import { api } from "../data";
import {
  TESTTAGE, fetchSpeicher, fetchTarifKatalog, fetchTarifStand, fetchZusatzpakete, grenzHinweise, imTarifKurz, kurzDatum, modulGesperrt, naechsterTarif, planName, tarifKeyMitModul,
  tarifMitModul, tarifName, upgradeText, wechselLink, type ModulKey, type Speichernutzung, type TarifKatalog, type TarifStand,
} from "../model/tarif";
import { t } from "../lib/sprache";
import { grenzeWachsenText, mehrpreisZusatz, tarifMitPreis } from "../model/testende";
import { Icon } from "./Icon";
import { useNurLesen } from "../app/nurLesen";

/**
 * Tarifstaffel in der Oberfläche (0099): Tarifstand und Katalog laden, Modul gesperrt?, Hinweis „Im Tarif Team enthalten“
 * mit „Upgrade auf Team“ an der gesperrten Aktion (K-374) und der Hinweis auf Tarifgrenzen für den Admin. Seiten und Menüs
 * bleiben sichtbar; nur die gesperrte Aktion ist deaktiviert (die Datenbank sperrt dieselben Schritte mit
 * „tarif_modul:<schlüssel>“). Dazu der kurze Tarifstand für Kontomenü und Tarifkarte (K-370, K-371).
 */

const ABO = "/agentur/einstellungen/abo";

/** Arbeitsbereich der Agentur oder null (außerhalb eines Agenturbereichs, z. B. in Einzeltests). */
export function useArbeitsbereich(): StaffWorkspace | null {
  try {
    return useStaffWorkspace();
  } catch {
    return null;
  }
}

/** Tarifstand der Agentur für alle Mitarbeiter (org_entitlements): Grenzen, Module, Nutzung. */
export function useTarifStand(orgId: string | null | undefined) {
  return useQuery({ queryKey: ["org_entitlements", orgId ?? "", "tarif"], enabled: !!orgId, queryFn: () => fetchTarifStand(api, orgId!) });
}

/** Tarifkatalog (plans, plan_entitlements), für den Namen des kleinsten Tarifs mit einem Modul. */
export function useTarifKatalog() {
  return useQuery({ queryKey: ["plans", "katalog", "tarif"], queryFn: () => fetchTarifKatalog(api), staleTime: 10 * 60_000 });
}

/** Belegter Speicher (org_storage_usage); derselbe Schlüssel wie die Abo-Seite, damit beide denselben Stand zeigen. */
export function useSpeicher(orgId: string | null | undefined, enabled = true) {
  return useQuery({ queryKey: ["storage", orgId ?? "", "abo"], enabled: enabled && !!orgId, queryFn: () => fetchSpeicher(api, orgId!).catch(() => null) });
}

/**
 * Ist ein Modul im Tarif der eigenen Agentur gesperrt? Solange der Tarifstand lädt (oder ohne Agenturbereich), gilt nichts als
 * gesperrt: Die Datenbank prüft ohnehin, und die Oberfläche soll nicht kurz alles sperren. `orgId` nur, wo die Seite ihre
 * Agentur selbst kennt (Einrichtung).
 */
export function useModul(key: ModulKey, orgId?: string): { gesperrt: boolean; geladen: boolean } {
  const ws = useArbeitsbereich();
  const q = useTarifStand(orgId ?? ws?.org.org_id ?? null);
  if (!q.data) return { gesperrt: false, geladen: false };
  return { gesperrt: modulGesperrt(q.data.limits, key), geladen: true };
}

/**
 * Schalter des Plattformbetriebs (platform_defaults), ob ein Modul schon freigeschaltet ist; fehlt er oder ist er nicht lesbar,
 * gilt „noch nicht“. Derselbe Abfrageschlüssel wie auf Abo & Tarif.
 */
export function usePlattformSchalter(key: string) {
  return useQuery({
    queryKey: ["platform_defaults", key],
    // K-444: über die Sicht talent_platform_defaults (0346), die auch reine Creator lesen dürfen
    queryFn: () => api.select<{ value: unknown }>("talent_platform_defaults", { columns: "value", filters: [["key", "eq", key]] })
      .then((r) => r[0]?.value === true || r[0]?.value === "true").catch(() => false),
  });
}

/**
 * K-375: Instagram-Insights warten auf die Prüfung durch Meta. Bis der Schalter instagram_insights_ready an ist, heißen sie
 * überall „in Vorbereitung“ und sind kein Grund für ein Upgrade. Solange der Schalter lädt: nicht bereit.
 */
export function useInsightsBereit(): boolean {
  return usePlattformSchalter("instagram_insights_ready").data === true;
}

/** K-375: einheitlicher Satz, solange Instagram-Insights noch nicht freigeschaltet sind */
export function insightsVorbereitungText(): string {
  return t("Instagram-Insights sind in Vorbereitung: Die Prüfung durch Meta läuft noch. Sobald sie freigeschaltet sind, sagen wir Bescheid.",
    "Instagram insights are in preparation: Meta's review is still in progress. We will let you know as soon as they are available.");
}

/** Name eines Tarifschlüssels aus dem Katalog (Oberflächensprache), sonst aus der festen Zuordnung. */
export function tarifAnzeige(key: string | null | undefined, katalog?: TarifKatalog | null): string {
  if (!key) return "";
  const p = katalog?.plans.find((x) => x.key === key);
  return p ? planName(p) : tarifName(key);
}

/**
 * Weg zum Tarif (K-374): Der Admin bekommt „Upgrade auf <Tarif>“; der Knopf öffnet auf Abo & Tarif direkt den Wechseldialog
 * mit dem nötigen Tarif (?wechsel=…). Alle anderen bekommen den Satz, dass der Admin den Tarif wählt. Ohne bekanntes Ziel:
 * „Tarife ansehen“ zur Abo-Seite.
 */
export function TarifAktion({ chef, ziel, name }: { chef: boolean; ziel?: string | null; name?: string | null }) {
  if (!chef) return <span>{t("Den Tarif wählt der Admin.", "The admin chooses the plan.")}</span>;
  if (ziel && name) return <Link className="btn primary tk-upgrade-knopf" to={wechselLink(ziel)}><Icon name="upgrade" />{upgradeText(name)}</Link>;
  return <Link className="btn" to={ABO}>{t("Tarife ansehen", "View plans")}</Link>;
}

/**
 * Hinweis an einer gesperrten Aktion (K-374, ein Wortlaut überall): Schloss, „Im Tarif Team enthalten“ und „Upgrade auf Team“.
 * Der Tarif kommt aus dem Katalog (wie app.require_module), bis er geladen ist aus der festen Zuordnung.
 * `id` verknüpft den Hinweis mit der deaktivierten Aktion (aria-describedby); `was` nennt die Funktion davor.
 * Instagram-Insights in Vorbereitung (K-375): kein Upgrade, nur der Hinweis.
 */
export function TarifHinweis({ modul, id, was, tarif }: { modul: ModulKey; id?: string; was?: string; tarif?: string | null }) {
  const ws = useArbeitsbereich();
  const katalog = useTarifKatalog();
  const insightsBereit = useInsightsBereit();
  // K-415: WhatsApp-Versand ist ebenfalls noch in Vorbereitung (Schalter whatsapp_sender_ready); kein Upgrade-Anreiz dafür
  const whatsappBereit = usePlattformSchalter("whatsapp_sender_ready").data === true;
  // K-497 i: Mehrpreis gegenüber dem eigenen Tarif („90,00 € netto mehr im Monat“); Hook vor jedem frühen Rücksprung
  const eigen = useTarifStand(ws?.isChef ? ws.org.org_id : null).data?.plan ?? null;
  // 0349: im Creator-Konto der passende Creator-Tarif (Creator Pro); hat ihn keiner, kein Upgrade (K-492)
  const creator = !!ws?.isCreator;
  const name = tarif ?? tarifMitModul(modul, katalog.data, creator);
  const ziel = tarifKeyMitModul(modul, katalog.data, creator);
  if (creator && !ziel) {
    return (
      <div className="notice row tk-tarif-hinweis" id={id} data-modul={modul}>
        <span className="tk-tarif-schloss"><Icon name="lock" /><span>{was ? <><strong>{was}:</strong> </> : null}{t("Im Creator-Tarif nicht enthalten", "Not included in the creator plans")}</span></span>
      </div>
    );
  }
  if ((modul === "module_insights" && !insightsBereit) || (modul === "module_whatsapp" && !whatsappBereit)) {
    return (
      <div className="notice row tk-tarif-hinweis" id={id} data-modul={modul}>
        <span>{was ? <><strong>{was}:</strong> </> : null}{modul === "module_whatsapp"
          ? t("WhatsApp-Hinweise sind in Vorbereitung: Der Versand startet in Kürze. Sobald er freigeschaltet ist, sagen wir Bescheid.", "WhatsApp notices are in preparation: sending starts soon. We will let you know as soon as it is available.")
          : insightsVorbereitungText()}</span>
      </div>
    );
  }
  return (
    <div className="notice row tk-tarif-hinweis" id={id} data-modul={modul}>
      {/* 0323 (Vertrieb 15.10.2026): Preis des Tarifs gleich am Hinweis, aus dem Katalog */}
      <span className="tk-tarif-schloss"><Icon name="lock" /><span>{was ? <><strong>{was}:</strong> </> : null}{imTarifKurz(name)}{mehrpreisZusatz(tarifMitPreis(ziel, katalog.data, "month"), tarifMitPreis(eigen, katalog.data, "month"))}{ws?.isChef ? "" : " ·"}</span></span>
      <TarifAktion chef={!!ws?.isChef} ziel={ziel} name={name} />
    </div>
  );
}

/**
 * Hinweis auf Tarifgrenzen (nur für den Admin): Creator, Team-Plätze (ohne den Platz des Inhabers, K-376), Verträge im Monat
 * und Speicher. Gelb erst, wenn ein Zähler voll ist (dann verhindert die Grenze das nächste Anlegen bzw. Hochladen); ab 80 %
 * ein neutraler Hinweis, auf Abo & Tarif (`nurVoll`) gar keiner, dort stehen die Zahlen neutral in der Tarifkarte. Unbegrenzte
 * Grenzen nie. Ohne `stand` lädt der Hinweis den Tarifstand (org_entitlements) und den Speicher selbst.
 */
export function TarifGrenzenHinweis({ orgId, stand, storage, nurVoll = false }: {
  orgId: string; stand?: (Pick<TarifStand, "limits" | "usage"> & { plan?: string | null }) | null; storage?: Speichernutzung | null; nurVoll?: boolean;
}) {
  const ws = useArbeitsbereich();
  const chef = !!ws?.isChef;
  const eigen = useTarifStand(chef && stand === undefined ? orgId : null);
  const speicher = useSpeicher(orgId, chef && storage === undefined);
  const katalog = useTarifKatalog();
  // P-3 (21.10.2026): in der Nur-Lesen-Phase und während einer Abo-Pause keine Wege zu Zusatzpaket oder Upgrade
  const nurLesen = useNurLesen();
  if (!chef || nurLesen) return null;
  const s = stand === undefined ? eigen.data ?? null : stand;
  if (!s) return null;
  // Creator-Konto (0178): ein Creator und ein Platz sind gewollt, keine Grenze zum Hinweisen
  const alle = grenzHinweise(s.limits, s.usage, storage === undefined ? speicher.data ?? null : storage)
    .filter((h) => !ws?.isCreator || (h.key !== "talents" && h.key !== "seats"));
  const hinweise = nurVoll ? alle.filter((h) => h.erreicht) : alle;
  if (!hinweise.length) return null;
  const erreicht = hinweise.some((h) => h.erreicht);
  const ziel = naechsterTarif(s.plan ?? null, { creator: !!ws?.isCreator, plans: katalog.data?.plans ?? null });
  const voll = hinweise.find((h) => h.erreicht && (h.key === "talents" || h.key === "seats"))?.key as "talents" | "seats" | undefined;
  return (
    <section className={`notice stack tk-tarif-grenzen${erreicht ? " warning" : ""}`} aria-labelledby="tarif-grenzen-titel">
      <strong id="tarif-grenzen-titel">{erreicht ? t("Eine Tarifgrenze ist erreicht", "A plan limit has been reached") : t("Tarifgrenzen fast erreicht", "Plan limits almost reached")}</strong>
      <ul>
        {hinweise.map((h) => <li key={h.key}>{h.text}</li>)}
      </ul>
      {/* K-490: volle Creator- bzw. Platzgrenze: beide Wege mit Preis (Zusatzpaket oder größerer Tarif) */}
      {voll
        ? <GrenzeWachsen art={voll} planKey={s.plan ?? null} />
        : <div className="row"><TarifAktion chef ziel={ziel} name={ziel ? tarifAnzeige(ziel, katalog.data) : null} /></div>}
    </section>
  );
}

/** Zusatzpakete (plan_addons) mit Preisen; derselbe Schlüssel überall */
export function useZusatzpakete() {
  return useQuery({ queryKey: ["plan_addons", "katalog"], queryFn: () => fetchZusatzpakete(api), staleTime: 10 * 60_000 });
}

/**
 * K-490: volle Creator- bzw. Platzgrenze. Statt nur „Upgrade auf Team“ beide Wege mit echten Preisen aus plans und plan_addons:
 * „Grenze erreicht. Weiter wachsen: 1 weiterer Creator für 6,00 € netto im Monat oder Upgrade auf Team für 129,00 € netto im
 * Monat (20 Creator, 3 Plätze).“ Zwei Knöpfe: Zusatzpaket buchen (Abschnitt „Zusätzliche Plätze und Creator“ auf Abo & Tarif)
 * und Upgrade (Wechseldialog mit dem Tarif vorgewählt). Nur der Admin bucht; alle anderen lesen, dass der Admin wählt.
 * Inline gebaut (span), damit es auch in einem Absatz stehen kann.
 */
export function GrenzeWachsen({ art, planKey }: { art: "talents" | "seats"; planKey?: string | null }) {
  const ws = useArbeitsbereich();
  const katalog = useTarifKatalog();
  const zusatz = useZusatzpakete();
  const stand = useTarifStand(planKey === undefined && ws ? ws.org.org_id : null);
  const plan = planKey === undefined ? stand.data?.plan ?? null : planKey;
  if (!ws?.isChef) return <span className="tk-grenze-wachsen"> {t("Mehr Creator oder Plätze bucht der Admin.", "The admin books more creators or seats.")}</span>;
  const paket = zusatz.data?.find((a) => a.entitlement_key === (art === "talents" ? "max_talents" : "max_seats")) ?? null;
  const zielKey = naechsterTarif(plan, { creator: !!ws.isCreator, plans: katalog.data?.plans ?? null });
  const grenze = (k: string) => katalog.data?.entitlements.find((e) => e.plan_key === zielKey && e.key === k)?.value ?? null;
  const ziel = zielKey ? { key: zielKey, name: tarifAnzeige(zielKey, katalog.data), cents: tarifMitPreis(zielKey, katalog.data, "month")?.cents ?? null, talents: grenze("max_talents"), seats: grenze("max_seats") } : null;
  return (
    <span className="tk-grenze-wachsen">
      {" "}{grenzeWachsenText({ art, zusatzCents: paket ? paket.price_month_cents : null, ziel })}
      <span className="row">
        {paket && <Link className="btn" to={`${ABO}#zusatzpakete`}>{art === "talents" ? t("Weiteren Creator buchen", "Book another creator") : t("Weiteren Platz buchen", "Book another seat")}</Link>}
        {ziel && <Link className="btn primary tk-upgrade-knopf" to={wechselLink(ziel.key)}><Icon name="upgrade" />{upgradeText(ziel.name)}</Link>}
        {!paket && !ziel && <Link className="btn" to={ABO}>{t("Abo & Tarif", "Subscription & plan")}</Link>}
      </span>
    </span>
  );
}

/** Dauer der Testphase in Tagen (platform_defaults.trial_days), bis sie gelesen ist der Standard der Datenbank */
export function useTesttage(): number {
  const q = useQuery({
    queryKey: ["platform_defaults", "trial_days"], staleTime: 10 * 60_000,
    queryFn: () => api.select<{ value: unknown }>("platform_defaults", { columns: "value", filters: [["key", "eq", "trial_days"]] })
      .then((r) => { const n = Number(r[0]?.value); return Number.isInteger(n) && n > 0 ? n : TESTTAGE; }).catch(() => TESTTAGE),
  });
  return q.data ?? TESTTAGE;
}

/**
 * Upgrade-Ziel der eigenen Agentur für Grenzen ohne bestimmtes Modul (Speicher, Creator, Plätze; K-374): nur für Admins einer
 * Agentur und nur mit größerem buchbarem Tarif, sonst null. `to` öffnet den Wechseldialog mit dem Tarif vorgewählt.
 */
export function useUpgradeZiel(): { key: string; name: string; to: string; label: string } | null {
  const ws = useArbeitsbereich();
  const q = useTarifStand(ws?.isChef ? ws.org.org_id : null);
  const katalog = useTarifKatalog();
  if (!ws?.isChef || ws.isCreator) return null;
  const key = naechsterTarif(q.data?.plan ?? null, { plans: katalog.data?.plans ?? null });
  if (!key) return null;
  const name = tarifAnzeige(key, katalog.data);
  return { key, name, to: wechselLink(key), label: upgradeText(name) };
}

/** Datum ohne Jahr für die kleine Tarifkarte: „24.10.“ bzw. „24/10“; andere Formate bleiben vollständig. */
export { kurzDatum };

export interface TarifKurzstand {
  /** Anzeigename des Tarifs (Badge im Kontomenü) */
  name: string | null;
  planKey: string | null;
  /** Schlüssel des nächsten größeren buchbaren Tarifs (nur Admins einer Agentur), sonst null */
  next: string | null;
  nextName: string | null;
  /** Ende der Testphase (ISO), solange sie läuft */
  testBis: string | null;
  creators: { used: number; max: number } | null;
  /** Admin einer Agentur (nicht Creator-Konto): nur dann Tarifkarte und Upgrade */
  admin: boolean;
  /** K-482 b: Zahlung fürs Abo gescheitert (offen oder deshalb gesperrt): statt „Upgrade“ der Weg „Zahlung nachholen“ */
  zahlungOffen?: boolean;
  /** 0372: im Test ohne Zahlungsart: so viele Tage Test extra beim Hinterlegen (sonst 0) */
  bonusTage?: number;
}

/**
 * Tarif der eigenen Agentur kurz (K-370, K-371): Name, nächster Tarif fürs Upgrade (nur Admins, nicht im Creator-Konto),
 * Testphase, Creator-Belegung. `life` ist der Lebenszyklus aus dem Rahmen; ohne Agenturbereich oder Tarif: null.
 */
export function useTarifKurzstand(ws: StaffWorkspace | null, life?: {
  status?: string | null; trial_ends_at?: string | null; plan_key?: string | null;
  subscription_status?: string | null; payment_failed_at?: string | null; read_only?: boolean; suspended_reason?: string | null;
  stripe_trial?: boolean; paypal_managed?: boolean; payment_bonus_days?: number | null;
} | null): TarifKurzstand | null {
  const q = useTarifStand(ws?.org.org_id ?? null);
  const katalog = useTarifKatalog();
  if (!ws) return null;
  const planKey = q.data?.plan ?? life?.plan_key ?? null;
  if (!planKey) return null;
  const testBis = life?.status === "trial" && !!life.trial_ends_at && Date.parse(life.trial_ends_at) > Date.now() ? life.trial_ends_at : null;
  const next = ws.isChef ? naechsterTarif(planKey, { creator: !!ws.isCreator, plans: katalog.data?.plans ?? null }) : null;
  const max = q.data?.limits.max_talents;
  return {
    name: tarifAnzeige(planKey, katalog.data),
    planKey,
    next,
    nextName: next ? tarifAnzeige(next, katalog.data) : null,
    testBis,
    creators: q.data?.usage && max !== undefined ? { used: q.data.usage.talents, max } : null,
    admin: ws.isChef && !ws.isCreator,
    bonusTage: testBis && !life?.stripe_trial && !life?.paypal_managed ? Math.max(0, Number(life?.payment_bonus_days ?? 0) || 0) : 0,
    zahlungOffen: life?.subscription_status === "past_due" || (!!life?.read_only && life?.status === "suspended" && (!!life?.payment_failed_at || /zahlung/i.test(life?.suspended_reason ?? ""))),
  };
}

/**
 * Kleine Tarifkarte (K-371): unten in der Seitenleiste (Desktop) bzw. oben in „Mehr“ (Handy), nur für Admins einer Agentur.
 * „Tarif Solo · 0/5 Creator“ mit „Upgrade“ (öffnet den Wechseldialog mit dem nächsten Tarif); in der Testphase
 * „Test bis TT.MM. · Tarif wählen“. Im Creator-Konto keine Karte.
 */
export function TarifKarte({ stand, variante = "leiste", timeZone }: { stand: TarifKurzstand | null; variante?: "leiste" | "mehr"; timeZone?: string }) {
  if (!stand?.admin) return null;
  if (stand.testBis) {
    return (
      <div className={variante === "mehr" ? "tk-tarifkarte tk-tarifkarte-mehr" : "tk-tarifkarte tk-tarifkarte-leiste"} role="group" aria-label={t("Dein Tarif", "Your plan")}>
        <span className="tk-tarifkarte-text"><strong>{t(`Test bis ${kurzDatum(stand.testBis, timeZone)}`, `Trial until ${kurzDatum(stand.testBis, timeZone)}`)}</strong>
          {/* 0372: wer im Test die Zahlungsart hinterlegt, bekommt einmal Tage dazu */}
          {!!stand.bonusTage && <small className="tk-tarifkarte-bonus">{t(`Mit Zahlungsart ${stand.bonusTage} Tage extra`, `${stand.bonusTage} extra days with a payment method`)}</small>}</span>
        <Link className="btn primary tk-tarifkarte-knopf" to={wechselLink(stand.planKey ?? "start")}>{t("Tarif wählen", "Choose plan")}</Link>
      </div>
    );
  }
  const max = stand.creators?.max;
  const zahl = stand.creators ? `${stand.creators.used}/${max !== undefined && max < 0 ? "∞" : max}` : "";
  const belegung = stand.creators ? t(` · ${zahl} Creator`, ` · ${zahl} creators`) : "";
  return (
    <div className={variante === "mehr" ? "tk-tarifkarte tk-tarifkarte-mehr" : "tk-tarifkarte tk-tarifkarte-leiste"} role="group" aria-label={t("Dein Tarif", "Your plan")}>
      <span className="tk-tarifkarte-text"><strong>{t(`Tarif ${stand.name ?? ""}`, `${stand.name ?? ""} plan`)}</strong>{belegung}</span>
      {stand.zahlungOffen
        ? <Link className="btn primary tk-tarifkarte-knopf" to="/zahlungsmethode">{t("Zahlung nachholen", "Settle payment")}</Link>
        : stand.next && stand.nextName
        ? <Link className="btn primary tk-tarifkarte-knopf" to={wechselLink(stand.next)} aria-label={upgradeText(stand.nextName)}><Icon name="upgrade" />{t("Upgrade", "Upgrade")}</Link>
        : <Link className="tk-tarifkarte-link" to={ABO}>{t("Abo & Tarif", "Subscription & plan")}</Link>}
    </div>
  );
}
